import type { ITheme } from "@app/app/components/ThemeProvider";
import { documentObserver } from "@app/app/services/documentObserver";
import i18next from "@app/app/services/i18n";
import { storageManager } from "@app/app/services/storageManager";
import { IGuestDetails } from "@app/booking/types";
import { StorageKeys as CheckoutStorageKeys } from "@app/checkout/constants/StorageKeys";
import { useGetCommonTranslations, useGetTranslations } from "@app/common/hooks";
import { get, merge, set } from "@app/common/utils/objectUtils";
import { IPropertyTheme } from "@app/property/types";
import { DocumentObserverEventTypes, UtilsIdentifier } from "@hotelchamp/common";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMachine } from "react-robot";

import { AppZIndexes } from "../constants/AppZIndexes";
import { StorageKeys } from "../constants/StorageKeys";
import { usePropertyContext } from "../hooks";
import { useTheme } from "../hooks/useTheme";
import { appElementsProvider } from "../services/appElements";
import { appStateMachine } from "../services/appStateMachine";
import { hostService } from "../services/host/HostService";
import { languageDetector } from "../services/languageDetector";
import { log } from "../services/log";
import { IAppState, IGlobalAppState, IIbeConfig } from "../types";
import { detectValidLanguageForProperty, detectValidLanguageForPropertyGroup, resolveValidLanguage } from "../utils/languageDetector";
import { constructUnifiedThemeConfig } from "../utils/themeUtils";
import { ibeConfigToUrlParams } from "../utils/url/ibeConfigToUrlParams";
import {
    AppStateContext,
    IAppStateContext,
    INITIAL_APP_STATE,
    INITIAL_GLOBAL_APP_STATE,
    IVisitedPageHistoryItem,
    TBookingResolver,
} from "./AppStateContext";

export interface IAuthLoaderProviderProps {
    children?: React.ReactNode | React.ReactNode[];
}

const IS_THEME_APPLIED: { [key: string]: { [key: string]: boolean } } = {}; // {propertyId: boolean} is thee applied for the active property

export function AppStateContextProvider({ children }: IAuthLoaderProviderProps) {
    const {
        propertyGroupId,
        activePropertyId,
        activeProperty,
        isActivePropertyFetched,
        isActivePropertyLoading,
        propertyGroup,
        allProperties,
        changeActivePropertyId,
    } = usePropertyContext();
    const [stateMachineState, send] = useMachine(appStateMachine);
    const { setTheme } = useTheme();
    const [visitedPageHistory, setVisitedPageHistory] = useState<IAppStateContext["visitedPageHistory"]>([]);
    const { data: commonTranslations } = useGetCommonTranslations();
    const { data: propertyTranslations } = useGetTranslations(activePropertyId, { enabled: !!activePropertyId });
    const [state, setState] = useState<IAppState>({} as IAppState);
    const [globalState, setGlobalState] = useState<IGlobalAppState>({} as IGlobalAppState);
    const [isSessionStateInitialized, setIsSessionStateInitialized] = useState<boolean>(false);
    const [isGlobalStateInitialized, setIsGlobalStateInitialized] = useState<boolean>(false);
    const [restorableGuestDetails, setRestorableGuestDetails] = useState<null | IGuestDetails>(null);
    const sessionStorage = storageManager.getGlobalSession();
    const currentWidgetState = stateMachineState.name;
    const isWidgetStateHidden = currentWidgetState === "hidden" || currentWidgetState === "inactive";
    const activeLanguage = state?.language;
    const isInitialized = isSessionStateInitialized && isGlobalStateInitialized;
    const ibeInstanceKey = appElementsProvider.getIbeInstanceKey()!;
    const [isMainLoyaltyModalOpen, setIsMainLoyaltyModalOpen] = useState<boolean>(false);

    const urlParams = new URLSearchParams(window.location.search);
    const appliedPromoCode = urlParams.get("pc");

    const ibeConfigUpdater = (config: IIbeConfig) => setState((oldState) => ({ ...oldState, initConfig: config }));
    const setConfigRef = useRef(ibeConfigUpdater);
    setConfigRef.current = ibeConfigUpdater;

    useEffect(() => {
        hostService.registerHostApiFunction("getWidgetState", () => stateMachineState.name);
        hostService.registerHostApiFunction("requestWidgetTransition", send);
    }, [send, stateMachineState.name]);

    // TODO: this is a quick solution to support launching the IBE when a promo code is applied
    useEffect(() => {
        if (currentWidgetState === "hidden" && appliedPromoCode && isInitialized) {
            send("SHOW");
        }
    }, [currentWidgetState, appliedPromoCode, isInitialized]);

    useEffect(() => {
        if (!IS_THEME_APPLIED[ibeInstanceKey]) {
            IS_THEME_APPLIED[ibeInstanceKey] = {};
        }
    }, [ibeInstanceKey]);

    // Restore the guest details from storage
    useEffect(() => {
        sessionStorage.get(CheckoutStorageKeys.CheckoutGuestDetailsState).then((nextRestorableGuestDetails) => {
            setRestorableGuestDetails(nextRestorableGuestDetails || null);
        });
    }, []);

    const detectAndSetLanguage = useCallback(() => {
        const languageDetectionResult = detectValidLanguageForProperty(languageDetector, activeProperty);
        const languageDetectionResultPropertyGroup = detectValidLanguageForPropertyGroup(languageDetector, propertyGroup || undefined);

        const { resolvedLanguage: resolvedLanguageSingleProperty } = languageDetectionResult;
        const { resolvedLanguage: resolvedLanguageMultiProperty } = languageDetectionResultPropertyGroup;
        const languageHasNotbeenSet =
            !activeLanguage ||
            resolvedLanguageSingleProperty === activeLanguage ||
            (propertyGroup && resolvedLanguageMultiProperty === activeLanguage);

        // Multi-property configuration
        if (propertyGroup && !activeProperty && languageHasNotbeenSet) {
            // set language to detected language
            setState((currentState) => ({
                ...currentState,
                activeLanguage: languageDetectionResultPropertyGroup.resolvedLanguage,
                language: languageDetectionResultPropertyGroup.resolvedLanguage,
            }));

            log.debug("AppStateContextProvider - multi-property detectAndStoreLanguage result", languageDetectionResult);
        }

        // Single property configuration
        if (!propertyGroup && resolvedLanguageSingleProperty && languageHasNotbeenSet) {
            setState((currentState) => ({
                ...currentState,
                activeLanguage: resolvedLanguageSingleProperty,
                language: resolvedLanguageSingleProperty,
            }));

            log.debug("AppStateContextProvider - detectAndStoreLanguage result", languageDetectionResult);
        }
    }, [setState, activeLanguage, activeProperty, propertyGroup]);

    const registerVisitedPage = useCallback(
        (visitedPage: IVisitedPageHistoryItem) => {
            setVisitedPageHistory((currentHistory) => {
                const newHistory = [...currentHistory, visitedPage];

                // Limit the history to the last 10 pages
                if (newHistory.length > 10) {
                    newHistory.shift();
                }

                return newHistory;
            });
        },
        [setVisitedPageHistory]
    );

    const setLanguage = useCallback(
        (nextLanguage: string) => {
            if (!activeProperty) {
                log.warn("Property is not loaded yet");

                return;
            }

            const languageResolvingResult = resolveValidLanguage(
                nextLanguage,
                activeProperty?.languages,
                activeProperty?.settings.default_language.code
            );

            if (!languageResolvingResult.didUseFallback) {
                log.debug("AppStateContextProvider - setLanguage result", languageResolvingResult);

                setState((currentState) => ({ ...currentState, language: languageResolvingResult.resolvedLanguage }));
            }
        },
        [setState, activeLanguage, activeProperty, propertyGroup]
    );

    useEffect(() => {
        hostService.registerHostApiFunction("detectLanguage", detectAndSetLanguage);
    }, [detectAndSetLanguage]);

    useEffect(() => {
        hostService.registerHostApiFunction("setLanguage", setLanguage);
    }, [setLanguage]);

    useEffect(() => {
        log.debug("AppStateContextProvider - state updated", state);
    }, [state]);

    const applyTheme = useCallback((id: string, propertyTheme?: IPropertyTheme, themeConfig?: ITheme) => {
        const theme = constructUnifiedThemeConfig(propertyTheme, themeConfig);

        const hasThemeBeenAppliedSuccessfully = setTheme(theme);

        if (hasThemeBeenAppliedSuccessfully) {
            IS_THEME_APPLIED[ibeInstanceKey][id] = true;
        }
    }, []);

    // Apply the property theme styles
    useEffect(() => {
        if (activeProperty && !IS_THEME_APPLIED[ibeInstanceKey][activePropertyId]) {
            applyTheme(activePropertyId, activeProperty.theme, activeProperty?.theme_config);
        }
    }, [activeProperty]);

    useEffect(() => {
        if (
            !activeProperty &&
            propertyGroupId &&
            propertyGroup &&
            propertyGroup.fallback_theme_config &&
            !IS_THEME_APPLIED[ibeInstanceKey][propertyGroupId]
        ) {
            applyTheme(activePropertyId, propertyGroup?.fallback_theme, propertyGroup?.fallback_theme_config);
        }
    }, [activeProperty, applyTheme, propertyGroup, propertyGroup?.fallback_theme_config, propertyGroupId]);

    useEffect(() => {
        hostService.registerHostApiFunction("openLoyaltyModal", (bool, includeIbe) => {
            if (includeIbe) {
                send(bool ? "SHOW" : "MINIMISE");
            }
            setIsMainLoyaltyModalOpen(bool);
        });
    }, [send, setIsMainLoyaltyModalOpen]);

    useEffect(() => {
        hostService.setHostApiValue("isInitialized", isInitialized);
    }, [isInitialized]);

    useEffect(() => {
        hostService.registerHostApiFunction("getAppZIndexes", () => AppZIndexes);
    }, []);

    // set state in storage on change
    useEffect(() => {
        const isEmptyState = !Object.keys(state).length;

        if (!isEmptyState) {
            // set state in storage but remove "property" because to prevent storage limit (since the size of the object)
            storageManager.getExtendedGlobalSession().set(StorageKeys.AppState, state);
        }
    }, [state]);

    // set global state in storage on change
    useEffect(() => {
        const isEmptyGlobalState = !Object.keys(globalState).length;

        if (!isEmptyGlobalState) {
            storageManager.getGlobal().set(StorageKeys.AppState, { ...globalState });
        }
    }, [globalState]);

    /**
     * When the ibe state value changes, sync it with storage so that
     * the state can be restored once the page reloads
     */
    useEffect(() => {
        if (!isWidgetStateHidden && isInitialized) {
            setState((prevAppState) => ({ ...prevAppState, restoreableWidgetState: currentWidgetState }));
        }
    }, [currentWidgetState, isInitialized]);

    // Restore state from storage initially when component is mount
    useEffect(() => {
        storageManager
            .getExtendedGlobalSession()
            .get(StorageKeys.AppState, INITIAL_APP_STATE)
            .then((storageAppState) => {
                const sessionId = storageAppState.sessionId || (UtilsIdentifier.uuid() as string);

                let restoreableWidgetState = storageAppState.restoreableWidgetState || "hidden";
                // if the search params are set and the init config is not set, we need to force the widget to be visible
                const hasBookingRate = urlParams.get("br") || urlParams.get("br[]") || urlParams.get("br[0]");
                if (!storageAppState.restoreableWidgetState && hasBookingRate) {
                    restoreableWidgetState = "visible";
                }
                // if the promo code is set, we need to force the widget to be visible
                if (!!appliedPromoCode) {
                    restoreableWidgetState = "visible";
                }

                if (urlParams.get("fo")) {
                    restoreableWidgetState = "visible";
                }

                let searchParams =
                    !storageAppState.initConfig && !storageAppState.searchParams ? "?p=base&c=search" : storageAppState.searchParams;

                if (!!isActivePropertyFetched && !activePropertyId) {
                    searchParams = "?p=base&c=property-select";
                }

                setState((currentState) => merge(currentState, { ...storageAppState, restoreableWidgetState, sessionId, searchParams }));

                setIsSessionStateInitialized(true);
            });
    }, []);

    useEffect(() => {
        storageManager
            .getGlobal()
            .get(StorageKeys.AppState, INITIAL_GLOBAL_APP_STATE)
            .then((storageGlobalAppState) => {
                const visitorId = storageGlobalAppState.visitorId || (UtilsIdentifier.uuid() as string);

                const promoCode = appliedPromoCode || storageGlobalAppState.promoCode;

                setGlobalState((currentGlobalState) => merge(currentGlobalState, { ...storageGlobalAppState, visitorId, promoCode }));

                setIsGlobalStateInitialized(true);
            });
    }, []);

    // Load and initialize translations
    useEffect(() => {
        const propertyGroupPropertyTranslations = {};

        (allProperties || []).forEach((item) => {
            const descriptions = item.description || {};

            Object.keys(descriptions).forEach((languageCode) => {
                // Please note, when there are no translations for a property description, then 'descriptions' is a string instead of an {"en-US": "some description"}
                const descriptionPerLanguage = typeof descriptions === "object" ? get(descriptions, languageCode) : descriptions;
                set(propertyGroupPropertyTranslations, `${languageCode}.description.${item.id}`, descriptionPerLanguage);
            });
        });

        const trs = merge(commonTranslations || {}, propertyTranslations || {}, propertyGroupPropertyTranslations);

        Object.keys(trs).forEach((lang) => {
            const langTrs = trs[lang] || ({} as any);

            Object.keys(langTrs).forEach((namespace) => {
                i18next.addResourceBundle(lang, namespace, langTrs[namespace] as any);
            });
        });
    }, [commonTranslations, propertyTranslations, activePropertyId, propertyGroup, allProperties]);

    useEffect(() => {
        if (isInitialized) {
            const documentObserverDetacher = documentObserver.on(DocumentObserverEventTypes.UrlChange, detectAndSetLanguage);

            detectAndSetLanguage();

            return documentObserverDetacher;
        }
    }, [detectAndSetLanguage, isInitialized]);

    const value: IAppStateContext = {
        state,
        globalState,
        setState: useCallback(setState, [setState]),
        requestWidgetTransition: send,
        widgetState: stateMachineState,
        isActivePropertyLoading,
        isInitialized,
        setGlobalState: useCallback(setGlobalState, [setGlobalState]),
        guestDetails: restorableGuestDetails,
        visitedPageHistory,
        registerVisitedPage,
        isMainLoyaltyModalOpen,
        setIsMainLoyaltyModalOpen,
    };

    return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
